import { useEffect, useState } from "react";
import DetailItem from "../DetailItem";
import { useWdrStatusForAsset } from "../../../../data-roaming/hooks";
import {
  CheckCircle as CheckCircleIcon,
  ErrorOutline as AlertIcon,
  Warning as WarningIcon,
} from "../../../../_app/components/icons";
import { UIBox, UIChip, UILoader, UITypography } from "../../../../_app/components";
import WorldwideDataCapInfo from "./WorldwideDataCapInfo";
import WorldwideDataCapToggle from "./WorldwideDataCapToggle";
import { WdrStatus } from "../../../../data-roaming/types";
import { createStylesheet } from "../../../../_app/utils/styles";

interface Props {
  assetId: string;
}

const WorldwideDataCapDetail = ({ assetId }: Props) => {
  const classes = useStyles();
  // Used store because capped setting is updated sooner than pendingChange
  const [capped, setCapped] = useState<boolean | undefined>(undefined);
  const [initialLoad, setInitialLoad] = useState(true);

  const { data, isLoading, isFetching } = useWdrStatusForAsset(assetId, {
    // Polling interval is set to 1 second if there is a pending change so we can fetch the latest status automatically after the change
    refetchInterval: (query: { state: { data?: WdrStatus } }) => {
      if (query.state.data?.pendingChange) {
        return 1000;
      }
      return false;
    },
    onSuccess: (data: WdrStatus) => {
      if (data?.pendingChange === false || initialLoad) {
        setInitialLoad(false);
      }
    },
  });

  useEffect(() => {
    if (data?.pendingChange === false || initialLoad) {
      setCapped(data?.wdrValue);
    }
  }, [data, initialLoad]);

  const pendingChange = data?.pendingChange || isFetching;
  const showToggle = !!data && capped !== undefined && !isLoading;

  const getValue = () => {
    // Show loader only for initial fetching
    if (isLoading) {
      return <UILoader data-cy={"wdr-loader"} size={25} />;
    }

    if (!data || capped === undefined) {
      return (
        <UIBox display="flex" alignItems="center">
          <AlertIcon className={classes.alertIcon} />
          <UITypography variant="subtitle2" className={classes.alertText}>
            No information available
          </UITypography>
        </UIBox>
      );
    }

    return (
      <UIChip
        icon={capped ? <WarningIcon /> : <CheckCircleIcon />}
        label={capped ? "Capped" : "Uncapped"}
        color={capped ? "warning" : "success"}
        variant="outlined"
      />
    );
  };

  const title = (
    <UIBox>
      Worldwide Data Cap
      <WorldwideDataCapInfo />
    </UIBox>
  );

  return (
    <DetailItem
      title={title}
      value={getValue()}
      action={
        showToggle ? (
          <WorldwideDataCapToggle pendingChange={pendingChange} capped={Boolean(capped)} assetId={assetId} />
        ) : undefined
      }
    />
  );
};

const useStyles = createStylesheet(() => ({
  alertText: {
    color: "red",
  },
  alertIcon: {
    color: "red",
    marginRight: "8px",
  },
}));

export default WorldwideDataCapDetail;
